import { useState, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";
import { JobsFilterWrapper } from "@/components/jobs/JobsFilterWrapper";
import { JobsContent } from "@/components/jobs/JobsContent";
import { JobsActions } from "@/components/jobs/JobsActions";
import { useJobsData } from "@/hooks/jobs/useJobsData";
import { usePersistedViewMode } from "@/utils/viewMode";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

const JobOffers = () => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewMode, setViewMode] = usePersistedViewMode("jobs");
  
  const {
    jobs,
    isLoading,
    searchTerm,
    setSearchTerm,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    addJob,
    fetchJobs
  } = useJobsData();

  // Create a memoized refresh function to avoid unnecessary re-renders
  const handleRefresh = useCallback(() => {
    console.log("Manual refresh triggered");
    fetchJobs();
  }, [fetchJobs]);

  const handleAddJob = async (newJobData: any) => {
    console.log("JobOffers handleAddJob called with data:", newJobData);
    try {
      // Make sure ai_summary is properly set
      if (newJobData.aiSummary && !newJobData.ai_summary) {
        newJobData.ai_summary = newJobData.aiSummary;
      }
      
      const jobId = await addJob(newJobData);
      if (jobId) {
        console.log("Job created successfully with ID:", jobId);
        setIsAddModalOpen(false);
        // Refresh the job list after adding a new job
        handleRefresh();
        toast({
          title: t("common.success", "Success"),
          description: t("jobs.formWizard.createSuccess", "Job offer has been created successfully")
        });
      } else {
        console.error("Job creation failed - no ID returned");
        toast({
          title: t("common.error", "Error"),
          description: t("jobs.formWizard.createError", "Failed to create job offer"),
          variant: "destructive"
        });
      }
      return jobId;
    } catch (error) {
      console.error("Error in job creation:", error);
      toast({
        title: t("common.error", "Error"),
        description: t("jobs.formWizard.createError", "Failed to create job offer: ") + (error instanceof Error ? error.message : t("errors.generic", "Unknown error")),
        variant: "destructive"
      });
      return undefined;
    }
  };

  return (
    <div className="w-full">
      <PageHeader 
        title={t("jobs.header", "Open Offers")}
        actions={
          <JobsActions 
            onAddClick={() => setIsAddModalOpen(true)} 
            onJobsGenerated={fetchJobs}
          />
        }
      />
      
      <JobsFilterWrapper 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortField={sortField as SortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />
      
      <JobsContent 
        jobs={jobs}
        isLoading={isLoading}
        viewMode={viewMode}
        onRefresh={handleRefresh}
        refreshInterval={15000} // Refresh every 15 seconds
        enableAutoRefresh={false} // Disable auto-refresh for the listing page
      />
      
      <JobOffersAddModal 
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddJob={handleAddJob}
      />
    </div>
  );
};

export default JobOffers;
